import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import api from '../api';
import { UserContext } from '../context/UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faFilter, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import ReactModal from 'react-modal';
import { toast } from 'sonner';
import { formatRut } from '../utils/formatRut';
import { Link } from 'react-router-dom';

ReactModal.setAppElement('#root');

// Mapeo de estados a iconos y colores
const estadosMap = {
    aceptada: { icon: '🟢', label: 'Aceptada', color: 'bg-green-100 text-green-800', borderColor: 'border-green-300' },
    aceptada_con_devolucion: { icon: '💰', label: 'Aceptada con Devolución', color: 'bg-emerald-100 text-emerald-800', borderColor: 'border-emerald-300' },
    devolucion_en_proceso: { icon: '💸', label: 'Devolución en Proceso', color: 'bg-teal-100 text-teal-800', borderColor: 'border-teal-300' },
    observada: { icon: '🟡', label: 'Observada', color: 'bg-yellow-100 text-yellow-800', borderColor: 'border-yellow-300' },
    rechazada: { icon: '🔴', label: 'Rechazada', color: 'bg-red-100 text-red-800', borderColor: 'border-red-300' },
    recibida: { icon: '🟡', label: 'Recibida', color: 'bg-blue-100 text-blue-800', borderColor: 'border-blue-300' },
    pago_pendiente: { icon: '⏳', label: 'Pago Pendiente', color: 'bg-orange-100 text-orange-800', borderColor: 'border-orange-300' },
    no_declarada: { icon: '⚪', label: 'No Declarada', color: 'bg-gray-100 text-gray-800', borderColor: 'border-gray-300' },
    sin_clave: { icon: '🔒', label: 'Sin Clave', color: 'bg-gray-200 text-gray-700', borderColor: 'border-gray-400' },
    no_hacer: { icon: '⛔', label: 'No Hacer', color: 'bg-slate-100 text-slate-700', borderColor: 'border-slate-300' },
    error: { icon: '❌', label: 'Error', color: 'bg-red-50 text-red-600', borderColor: 'border-red-200' }
};

const EstadosRenta = () => {
    const { currentUser } = useContext(UserContext);
    const [estados, setEstados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [años, setAños] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedEstado, setSelectedEstado] = useState('');
    const [search, setSearch] = useState('');
    const [isScrapingRunning, setIsScrapingRunning] = useState(false);
    const [scrapingProgress, setScrapingProgress] = useState(null); // { procesados, total }
    const [scrapingIntervalRef, setScrapingIntervalRef] = useState(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedCliente, setSelectedCliente] = useState(null);

    useEffect(() => {
        fetchEstados();
        generateYears();
    }, [selectedYear]);

    const generateYears = () => {
        const currentYear = new Date().getFullYear();
        const yearList = [];
        for (let i = currentYear; i >= currentYear - 5; i--) {
            yearList.push(i);
        }
        setAños(yearList);
    };

    const fetchEstados = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/estados-sii/${selectedYear}`);
            console.log('Estados SII recibidos:', response.data);
            setEstados(response.data);
        } catch (error) {
            console.error('Error al cargar estados SII:', error);
            toast.error('Error al cargar estados de declaraciones');
        } finally {
            setLoading(false);
        }
    };

    const handleDetenerScraping = () => {
        if (scrapingIntervalRef) {
            clearInterval(scrapingIntervalRef);
            setScrapingIntervalRef(null);
        }
        setIsScrapingRunning(false);
        setScrapingProgress(null);
        toast.info('Seguimiento detenido. El proceso en servidor puede continuar en background.');
    };

    const handleEjecutarScraping = async () => {
        const isAdmin = currentUser?.role === 'ADMIN';
        if (!isAdmin) return;

        const { value: confirmed } = await Swal.fire({
            title: '¿Ejecutar consulta al SII?',
            html: `Se consultará el estado de declaración de todas las empresas para el año <strong>${selectedYear}</strong> y los 2 años anteriores.<br><br>
                   <small>Este proceso puede tomar varios minutos.</small>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, ejecutar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#3085d6',
        });

        if (confirmed) {
            setIsScrapingRunning(true);
            setScrapingProgress({ procesados: 0, total: '...' });
            toast.info('Consulta iniciada. Actualizando cada 30 segundos...');

            try {
                await api.post('/estados-sii/ejecutar', { año: selectedYear });

                // Refrescar cada 30 segundos
                const interval = setInterval(async () => {
                    await fetchEstados();
                    // Consultar log más reciente para mostrar progreso
                    try {
                        const logRes = await api.get(`/log-scraping/ultimo/${selectedYear}`);
                        if (logRes.data) {
                            const { procesados, totalProcesar, estado } = logRes.data;
                            setScrapingProgress({ procesados, total: totalProcesar });
                            if (estado === 'completado' || estado === 'error') {
                                clearInterval(interval);
                                setScrapingIntervalRef(null);
                                setIsScrapingRunning(false);
                                setScrapingProgress(null);
                                toast.success(estado === 'completado' ? 'Consulta completada' : 'Consulta finalizada con errores');
                                fetchEstados();
                            }
                        }
                    } catch (_) {}
                }, 30000);

                setScrapingIntervalRef(interval);

            } catch (error) {
                console.error('Error al ejecutar scraping:', error);
                Swal.fire('Error', 'No se pudo iniciar la consulta: ' + (error.response?.data?.error || error.message), 'error');
                setIsScrapingRunning(false);
                setScrapingProgress(null);
            }
        }
    };

    const openModal = (cliente) => {
        setSelectedCliente(cliente);
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setSelectedCliente(null);
    };

    const filteredEstados = estados.filter(estado => {
        const matchSearch = !search || 
            estado.nombre?.toLowerCase().includes(search.toLowerCase()) ||
            estado.rut?.includes(search);
        
        const añoEstado = estado.estados?.find(e => e.año === selectedYear);
        const matchEstado = !selectedEstado || añoEstado?.estado === selectedEstado;

        return matchSearch && matchEstado;
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-xl">Cargando estados...</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            {/* Header */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-3xl font-bold text-gray-800">Estados de Declaración de Renta (SII)</h1>
                    {currentUser?.role === 'ADMIN' && (
                        <Link
                            to="/monitor-sii"
                            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                        >
                            <FontAwesomeIcon icon={faSync} />
                            Monitor / Ejecutar
                        </Link>
                    )}
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                        >
                            {años.map(año => (
                                <option key={año} value={año}>{año}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                        <select
                            value={selectedEstado}
                            onChange={(e) => setSelectedEstado(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                        >
                            <option value="">Todos</option>
                            {Object.entries(estadosMap).map(([key, value]) => (
                                <option key={key} value={key}>{value.icon} {value.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Nombre o RUT..."
                            className="w-full p-2 border border-gray-300 rounded-lg"
                        />
                    </div>
                </div>

                {/* Resumen */}
                <div className="mt-4 text-sm text-gray-600">
                    Mostrando <strong>{filteredEstados.length}</strong> de <strong>{estados.length}</strong> empresas
                </div>
            </div>

            {/* Leyenda */}
            <div className="bg-white shadow rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Leyenda de Estados</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(estadosMap).map(([key, value]) => (
                        <div key={key} className="flex items-center">
                            <span className="mr-2 text-lg">{value.icon}</span>
                            <span className="text-sm">{value.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Empresa
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    RUT
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Última Actualización
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Años Consultados
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredEstados.map((cliente) => {
                                const añoEstado = cliente.estados?.find(e => e.año === selectedYear);
                                const estadoInfo = estadosMap[añoEstado?.estado] || estadosMap.no_declarada;

                                return (
                                    <tr key={cliente._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${estadoInfo.color} ${estadoInfo.borderColor}`}>
                                                <span className="mr-2">{estadoInfo.icon}</span>
                                                {estadoInfo.label}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {cliente.nombre?.substring(0, 50)}
                                                {cliente.nombre?.length > 50 ? '...' : ''}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatRut(cliente.rut)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {cliente.ultimaActualizacion 
                                                ? new Date(cliente.ultimaActualizacion).toLocaleString('es-CL')
                                                : 'Sin datos'}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="text-sm text-gray-600">
                                                {cliente.estados?.length || 0} años
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button
                                                onClick={() => openModal(cliente)}
                                                className="text-blue-600 hover:text-blue-800 font-medium"
                                            >
                                                <FontAwesomeIcon icon={faFileAlt} className="mr-1" />
                                                Ver Detalle
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {filteredEstados.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            No se encontraron resultados
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Detalle */}
            <ReactModal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                className="max-w-4xl mx-auto mt-20 bg-white rounded-lg shadow-2xl p-6 outline-none"
                overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto"
            >
                {selectedCliente && (
                    <div>
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">{selectedCliente.nombre}</h2>
                                <p className="text-gray-600">{formatRut(selectedCliente.rut)}</p>
                            </div>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>

                        {/* Estados por año */}
                        <div className="space-y-4">
                            {selectedCliente.estados?.map((estado) => {
                                const estadoInfo = estadosMap[estado.estado] || estadosMap.no_declarada;

                                return (
                                    <div key={estado.año} className={`border-2 rounded-lg p-4 ${estadoInfo.borderColor}`}>
                                        <div className="flex justify-between items-center mb-3">
                                            <h3 className="text-lg font-semibold">Año {estado.año}</h3>
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${estadoInfo.color}`}>
                                                <span className="mr-2">{estadoInfo.icon}</span>
                                                {estadoInfo.label}
                                            </span>
                                        </div>

                                        {/* Mensaje situación */}
                                        {estado.mensajeSituacion && (
                                            <div className="mb-3 p-3 bg-gray-50 rounded">
                                                <p className="text-sm font-medium text-gray-700">Situación:</p>
                                                <p className="text-sm text-gray-600">{estado.mensajeSituacion}</p>
                                            </div>
                                        )}

                                        {/* Último evento */}
                                        {estado.ultimoEvento && (
                                            <div className="mb-3 p-3 bg-blue-50 rounded">
                                                <p className="text-sm font-medium text-blue-900">Último Evento:</p>
                                                <p className="text-xs text-blue-700">{estado.ultimoEvento.fecha}</p>
                                                <p className="text-sm text-blue-800 mt-1">{estado.ultimoEvento.descripcion}</p>
                                            </div>
                                        )}

                                        {/* Historial */}
                                        {estado.historial && estado.historial.length > 0 && (
                                            <div>
                                                <p className="text-sm font-medium text-gray-700 mb-2">Historial completo:</p>
                                                <div className="max-h-48 overflow-y-auto border rounded">
                                                    <table className="min-w-full text-xs">
                                                        <thead className="bg-gray-100 sticky top-0">
                                                            <tr>
                                                                <th className="px-2 py-1 text-left">Fecha</th>
                                                                <th className="px-2 py-1 text-left">Descripción</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {estado.historial.map((evento, idx) => (
                                                                <tr key={idx} className="border-t">
                                                                    <td className="px-2 py-1 whitespace-nowrap">{evento.fecha}</td>
                                                                    <td className="px-2 py-1">{evento.descripcion}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        <div className="mt-2 text-xs text-gray-500">
                                            Consultado: {estado.fechaConsulta ? new Date(estado.fechaConsulta).toLocaleString('es-CL') : 'Sin datos'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                )}
            </ReactModal>
        </div>
    );
};

export default EstadosRenta;
